import React, { useState, useMemo, useEffect } from "react";
import { Modal, ModalOverlay, ModalContent, useToast } from "@chakra-ui/react";
import { ChevronLeft, Wallet, Gift, Zap, Star } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { createRechargeOrder } from "../../services/walletService";
import { getAppControls } from "../../services/configService";

// Amount options with their bonus percentages
const AMOUNT_OPTIONS = [
  { amount: 50, extra: 0 },
  { amount: 100, extra: 0 },
  { amount: 200, extra: 0 },
  { amount: 500, extra: 5, popular: true },
  { amount: 1000, extra: 5 },
  { amount: 2000, extra: 10 },
  { amount: 3000, extra: 10 },
  { amount: 5000, extra: 12 },
];

// Bonus tiers for calculating next bonus threshold
const BONUS_TIERS = [
  { minAmount: 500, extra: 5 },
  { minAmount: 2000, extra: 10 },
  { minAmount: 4000, extra: 12 },
  { minAmount: 15000, extra: 15 },
  { minAmount: 50000, extra: 20 },
];

const RechargeModal = ({ isOpen, onClose, walletBalance = 0 }) => {
  const [amount, setAmount] = useState("9");
  const [selectedPreset, setSelectedPreset] = useState(50);
  const [loading, setLoading] = useState(false);
  const [minRechargeAmount, setMinRechargeAmount] = useState(0);
  const toast = useToast();

  // Fetch app controls on mount
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await getAppControls();
        if (response?.result?.minRechargeAmount) {
          setMinRechargeAmount(response.result.minRechargeAmount);
        }
      } catch (error) {
        console.log("Failed to fetch app controls:", error);
      }
    };
    fetchSettings();
  }, []);

  useEffect(() => {
    const defaultOption = AMOUNT_OPTIONS[0]; // first = 50
    // setAmount(String(defaultOption.amount));
    setSelectedPreset(defaultOption.amount);
  }, []);

  // Calculate the promotional message based on current amount
  const promoMessage = useMemo(() => {
    const currentAmount = Number(amount) || 0;

    for (const tier of BONUS_TIERS) {
      if (currentAmount < tier.minAmount) {
        const amountNeeded = tier.minAmount - currentAmount;
        return `Add ₹${amountNeeded} more to get extra value.`;
      }
    }
    return null; // Already at max tier
  }, [amount]);

  const handleAmountChange = (e) => {
    const value = e.target.value;
    setAmount(value);
    setSelectedPreset(null);
  };

  const handleQuickAdd = (addAmount) => {
    const currentAmount = Number(amount) || 0;
    const newAmount = currentAmount + addAmount;
    setAmount(String(newAmount));
    setSelectedPreset(null);
  };

  const handlePresetSelect = (preset) => {
    setAmount(String(preset.amount));
    setSelectedPreset(preset.amount);
  };

  const handleRecharge = async () => {
    if (!amount || Number(amount) < minRechargeAmount) {
      toast({
        title: "Invalid Amount",
        description: `Minimum recharge amount is ₹${minRechargeAmount}.`,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setLoading(true);
    try {
      const response = await createRechargeOrder(Number(amount));
      const order = response.result;

      if (order.upiLink) {
        window.open(order.upiLink, "_blank");
        toast({
          title: "Payment Link Opened",
          description: "Complete the payment in the new tab",
          status: "info",
          duration: 5000,
          isClosable: true,
        });
        onClose();
        setAmount("");
        setSelectedPreset(null);
      } else {
        toast({
          title: "Error",
          description: "Payment link not available",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.toString(),
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setAmount("");
    setSelectedPreset(null);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="md"
      isCentered
      scrollBehavior="inside"
    >
      <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(8px)" />
      <ModalContent
        position="fixed"
        bottom="0"
        left="0"
        right="0"
        mb="0"
        borderTopRadius="24px"
        bg="#fdf3fc"
        maxH="85vh"
        overflow="hidden"
        className="animate-slideUp"
      >
        <div className="flex justify-center py-2">
          <div className="w-12 h-1.5 bg-gray-300 rounded-full"></div>
        </div>
        {/* Header */}
        <div className="flex flex-col items-center justify-center pt-2 pb-3">
          {/* Icon Circle */}
          <div className="w-16 h-16 flex items-center justify-center bg-pink-100 rounded-full mb-2">
            <Wallet size={28} className="text-red-500" />
          </div>

          {/* Title */}
          <h2 className="text-2xl font-bold text-[#34163F]">Add Credits</h2>
          <p>(100 credit = ₹1)</p>
        </div>

        {/* Body */}
        <div className="p-4">
          {/* Enter Amount Section */}
          <p className="text-gray-400 text-sm mb-1">Select Amount</p>

          <div className="flex items-center gap-2 mb-5">
            <div className="flex-1 relative">
              <input
                type="number"
                placeholder="0"
                value={amount}
                onChange={handleAmountChange}
                className="w-full pl-8 pr-3 py-3 text-xl text-black bg-white border-b-2 border-yellow-300  rounded-xl focus:border-purple-500 outline-none transition-colors placeholder-gray-600"
              />
            </div>
            <button
              onClick={() => handleQuickAdd(50)}
              className="pl-4 pr-3 py-3 text-xl font-medium text-purple-300 border border-yellow-300 rounded-xl hover:bg-purple-500/10 transition-colors whitespace-nowrap"
            >
              + Rs50
            </button>
            <button
              onClick={() => handleQuickAdd(100)}
              className="pl-4 pr-3 py-3 text-xl font-medium text-purple-300 border border-yellow-300 rounded-xl hover:bg-purple-500/10 transition-colors whitespace-nowrap"
            >
              + Rs100
            </button>
          </div>

          {/* Promo Banner */}
          <AnimatePresence>
            {promoMessage && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="mb-5"
              >
                <div className="flex items-center gap-2.5 bg-[#34163F] text-white border border-amber-500/20 rounded-xl p-3">
                  <Gift size={18} className="text-amber-400 flex-shrink-0" />
                  <p className="text-white text-sm">{promoMessage}</p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Amount Grid */}
          <div className="grid grid-cols-4 gap-2 mt-4">
            {AMOUNT_OPTIONS.map((option) => {
              const isSelected = selectedPreset === option.amount;

              return (
                <button
                  key={option.amount}
                  onClick={() => handlePresetSelect(option)}
                  className={`relative py-3 rounded-xl text-center border transition-all
          ${
            isSelected
              ? "border-yellow-300 text-white bg-yellow-300"
              : "border-gray-200 bg-white"
          }
        `}
                >
                  {/* Amount */}
                  <p
                    className={`text-sm font-semibold ${
                      isSelected ? "text-white" : "text-gray-800"
                    }`}
                  >
                    Rs {option.amount}
                  </p>
                </button>
              );
            })}
          </div>
        </div>

        {/* Bottom Button */}
        <div className="p-4 border-t bg-[#fdf3fc] sticky bottom-0">
          <button
            onClick={handleRecharge}
            className="w-full bg-yellow-300 text-white py-3 rounded-full font-semibold"
          >
            Continue
          </button>

          <button
            onClick={handleClose}
            className="w-full mt-3 border border-[#34163F] text-gray-600 py-3 rounded-full"
          >
            MAYBE LATER
          </button>
          <div className="text-xs text-gray-500 text-center mt-2">
            Purchase credits are non-withdrawable and can only be used to access
            quizzes,contests, and learning features.
          </div>
        </div>
      </ModalContent>
    </Modal>
  );
};

export default RechargeModal;
